import { FC, useCallback, useEffect, useRef, useState } from "react";
import CustomPopup from "../../../components/CustomPopup/CustomPopup";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import AlertRhombus from "../../../assets/icons/alert-rhombus.svg";
import otpVerificationImage from "../../../assets/images/otp-verification-lock-4x.png";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../redux-store/CustomReduxHooks";
import {
  emailRegex,
  fcForEmailUpdateGenerateOtpApi,
  otpScreenErrorMsg,
  pageMessages,
} from "../../../utils/constants";
import CustomThreeDotsLoader from "../../../components/CustomThreeDotsLoader/CustomThreeDotsLoader";
import "./EditEmailPopup.scss";
import {
  useLazyGenerateOtpQuery,
  useLazyValidateOtpQuery,
} from "../../Feature_Login/Login_Api";
import { T_GenerateOtpPay } from "../../Feature_Login/Login_Types";
import { API_STATUS_CODE } from "../../../utils/ApiEndpoints";
import { setError } from "../../../redux-store/reducers/CsErrorSlice";
import { T_ApiResponse } from "../../../globalTypes/GlobalTypes";
import { Colors } from "../../../styles/constant";
import { MuiOtpInput } from "mui-one-time-password-input";
import { useLazyUpdateUserProfileDetailsQuery } from "../GeneralAppElements_Api";
import { updateUserProfileDetails } from "../../../redux-store/reducers/CsAppSlice";
import CustomCircularLoader from "../../../components/CustomCircularLoader/CustomCircularLoader";
import usePageMessages from "../../../hooks/useCmsPageMessages";
import { validateEmail } from "../../../utils/conversionUtils";

interface Props {
  open: boolean;
  handleOpen: any;
  setEmailUpdateComplete: any;
}

const EditEmailPopup: FC<Props> = ({
  open,
  handleOpen,
  setEmailUpdateComplete,
}) => {
  const masterData = useAppSelector(
    (state) => state.GeneralAppElementsSlice?.masterData
  );
  const masterDetails = masterData?.masterDetail;
  const wrongOtpCountAllowed = parseInt(
    masterDetails?.wrong_otp_count_allowed?.find(
      (item) => item.masterKey === "limit"
    )?.masterValue || "3"
  );
  const retryCount = parseInt(
    masterDetails?.retry_count?.find(
      (item) => item.masterKey === "otpBypassValue"
    )?.masterValue || "3"
  );

  const [popupWidth, setPopupWidth] = useState<string>("auto");
  const [isBtnLoading, setIsBtnLoading] = useState<string>("");
  const [helperText, setHelperText] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [screenType, setScreenType] = useState<
    "edit-email" | "verify-otp" | "note"
  >("edit-email");
  const [otpValue, setOtpValue] = useState<string>("");
  const [count, setCount] = useState<number>(60);
  const [otpErrorMessage, setOtpErrorMessage] = useState<string>("");
  const [otpVerifyAttemptLeft, setOtpVerifyAttemptLeft] =
    useState<number>(wrongOtpCountAllowed);
  const [reSendOtpAttemptLeft, setReSendOtpAttemptLeft] =
    useState<number>(retryCount);
  const csAppSlice = useAppSelector((state) => state.CsAppSlice);
  const userProfile = csAppSlice?.userDetails;
  const [email, setEmail] = useState<string>(userProfile?.emailId || "");
  const [generateOtpApi] = useLazyGenerateOtpQuery();
  const [validateOtpApi] = useLazyValidateOtpQuery();
  const [updateUserProfileDetailsApi] = useLazyUpdateUserProfileDetailsQuery();
  const editEmailNote = usePageMessages(pageMessages["ga-edit-email-note"]);

  const dispatch = useAppDispatch();
  const isTimerOn = useRef(true);
  const otpByPassValue =
    masterDetails?.otpBypass?.find(
      (item) => item.masterKey === "otpBypassValue"
    )?.masterValue || "";
  const isOtpBypassEnable =
    masterDetails?.otpBypass?.find((item) => item.masterKey === "otpBypassFlag")
      ?.masterValue || "";

  useEffect(() => {
    const element: any = document.getElementsByClassName("hamburger-menu-page");
    if (element.length > 0) {
      const width = element[0].clientWidth - 32;
      setPopupWidth(width.toString());
    }
  }, []);

  const handleSave = async () => {
    let reqPayload = {
      csUserId: csAppSlice.csUserId,
      name: userProfile?.name || "",
      pan: userProfile?.pan || "",
      dob: userProfile?.dob || "",
      mobile: userProfile?.mobileNumber || "",
      emailId: email,
    };
    await updateUserProfileDetailsApi(reqPayload).then((res) => {
      setIsBtnLoading("");
      if (res.isSuccess && res.data?.code === API_STATUS_CODE.SUCCESS) {
        let updatedData = { emailId: email };
        dispatch(updateUserProfileDetails(updatedData));
        setEmailUpdateComplete(true);
        handleOpen(false);
      }
      if (res.isError || res.data?.code !== API_STATUS_CODE.SUCCESS) {
        dispatch(
          setError({
            error: (res.error as T_ApiResponse<any>) || Error("Failed!"),
            message: "API failed",
          })
        );
      }
    });
  };

  const handleSendOTP = () => {
    setIsLoading(true);
    const requestParams: T_GenerateOtpPay = {
      mobileNo: userProfile?.mobileNumber,
      emailId: userProfile?.emailId || email,
      otp: "",
      functionality: fcForEmailUpdateGenerateOtpApi,
    };
    generateOtpApi(requestParams).then((res) => {
      setIsLoading(false);
      if (res.isError && res.error) {
        onGenerateOtpFailure(res.error);
      } else if (res.data?.code === API_STATUS_CODE.SUCCESS) {
        setScreenType("verify-otp");
        startTimer();
      } else {
        dispatch(
          setError({
            error: (res.error as T_ApiResponse<any>) || Error("Failed!"),
            message: "API failed",
          })
        );
      }
    });
  };

  const handleOnchange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setEmail(value);
    setHelperText(validateEmail(value) ? "" : "Please enter a valid email id");
  };

  const handleEmailBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setHelperText(validateEmail(value) ? "" : "Please enter a valid email id");
    return;
  };

  const handleOnChangeOTP = (value: string) => {
    setOtpValue(value);
    setOtpErrorMessage("");
  };

  const handleVerifyOtpClick = () => {
    setIsLoading(true);
    validateOtpApi({
      mobileNo: userProfile?.mobileNumber || "",
      otp: otpValue,
      functionality: fcForEmailUpdateGenerateOtpApi,
    }).then((res) => {
      setIsLoading(false);
      if (
        res.data?.code === 1 &&
        res.data.payload.mobileNo === userProfile?.mobileNumber
      ) {
        setScreenType("note");
      } else {
        setOtpErrorMessage("Incorrect OTP, please try again later");
        setOtpValue("");
        if (otpVerifyAttemptLeft > 0) {
          if (otpVerifyAttemptLeft === 1) {
            setOtpErrorMessage(otpScreenErrorMsg);
          }
          setOtpVerifyAttemptLeft((prevCount) => prevCount - 1);
        }
      }
    });
  };

  const startTimer = useCallback(() => {
    isTimerOn.current = true;
    const intervalId = setInterval(() => {
      setCount((prevCount) => {
        const newCount = prevCount - 1;
        if (newCount <= 0) {
          clearInterval(intervalId);
          isTimerOn.current = false;
          return 60;
        }
        return newCount;
      });
    }, 1000);
  }, []);

  const onGenerateOtpFailure = (error: any) => {
    const e = (error as T_ApiResponse<any>) || Error("Something went wrong");
    if (e?.code === API_STATUS_CODE.BUSINESS_ERROR) {
      setHelperText(otpScreenErrorMsg);
      setOtpErrorMessage(otpScreenErrorMsg);
      setReSendOtpAttemptLeft(0);
      setOtpVerifyAttemptLeft(0);
    } else {
      dispatch(
        setError({
          error: e,
          message: otpScreenErrorMsg,
        })
      );
    }
  };

  const handleResendOTP = () => {
    const requestParams: T_GenerateOtpPay = {
      emailId: userProfile?.emailId || email,
      mobileNo: userProfile?.mobileNumber || "",
      otp: "",
      functionality: fcForEmailUpdateGenerateOtpApi,
    };
    setOtpValue("");
    generateOtpApi(requestParams).then((res) => {
      if (res.isError && res.error) {
        onGenerateOtpFailure(res.error);
      } else if (!res.isError && res.data?.code === API_STATUS_CODE.SUCCESS) {
        setReSendOtpAttemptLeft((prev) => prev - 1);
        startTimer();
      } else {
        dispatch(
          setError({
            error: res.data,
            message: res.data?.message,
          })
        );
      }
    });
  };

  const handleNoteBackClick = () => {
    setIsBtnLoading("Back");
    handleOpen(false);
  };

  const handleConfirmClick = async () => {
    setIsBtnLoading("Confirm");
    await handleSave();
  };

  const renderScreens = () => {
    switch (screenType) {
      case "edit-email":
        return (
          <Box
            width={popupWidth + "px"}
            display="flex"
            flexDirection="column"
            gap="2rem"
          >
            <Typography variant="h2" lineHeight={"120%"}>
              Update Your Email Id
            </Typography>
            <TextField
              fullWidth
              onBlur={handleEmailBlur}
              label="Email Id"
              value={email}
              placeholder="Enter your email Id"
              name="email"
              onChange={handleOnchange}
              error={helperText.length > 0}
              helperText={
                <span style={{ display: "flex", alignItems: "center" }}>
                  <InputAdornment position="start">
                    {helperText.length > 0 && <img src={AlertRhombus} />}
                  </InputAdornment>
                  {helperText}
                </span>
              }
              inputProps={{
                maxLength: 254,
                inputMode: "text",
                pattern: emailRegex,
              }}
            />
            {isLoading ? (
              <CustomThreeDotsLoader />
            ) : (
              <Button
                fullWidth
                variant="contained"
                disabled={
                  email === userProfile?.emailId || !email || !!helperText
                }
                className="continue-btn"
                onClick={handleSendOTP}
              >
                Send OTP
              </Button>
            )}
          </Box>
        );

      case "verify-otp":
        return (
          <Box className="otp-verification" width={popupWidth + "px"}>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
            >
              <img
                src={otpVerificationImage}
                alt="otp"
                style={{ width: "4.8125rem", height: "4.8125rem" }}
              />
              <Typography textAlign="center" marginTop="1.5rem" variant="h2">
                OTP Verification
              </Typography>
              <Typography
                textAlign="center"
                marginTop=".25rem"
                variant="subtitle2"
                color={Colors.textGray}
                width="90%"
              >
                {/* Enter the 6 digit code sent to your{" "}
                <span style={{ color: Colors.red }}>email id</span> */}
                Enter the 6 digit code sent to{" "}
                <span style={{ color: Colors.red, fontWeight: 600 }}>
                  registered mobile number and email id
                </span>
              </Typography>
            </Box>
            <Box width={"90%"} marginX={"auto"}>
              <Box
                display="block"
                alignItems="center"
                flexDirection="column"
                justifySelf="center"
              >
                <MuiOtpInput
                  className={`otp-input ${
                    otpErrorMessage.length > 0 ? "error" : ""
                  }`}
                  length={6}
                  value={otpValue}
                  onChange={handleOnChangeOTP}
                  validateChar={(char) => /^[0-9]$/.test(char)}
                  TextFieldsProps={{
                    inputProps: {
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                    },
                  }}
                />
                {otpErrorMessage && (
                  <Box display="flex" gap={".25rem"} alignItems="center">
                    <img src={AlertRhombus} />
                    <Typography
                      marginY={"0.5rem"}
                      color={Colors.red}
                      variant="h6"
                    >
                      {otpErrorMessage}
                    </Typography>
                  </Box>
                )}
                {isTimerOn.current ? (
                  <Typography
                    marginTop=".75rem"
                    fontSize={14}
                    fontWeight={400}
                    color="#797979"
                  >
                    {`Resend OTP in 00:${count.toString().padStart(2, "0")}s`}
                  </Typography>
                ) : reSendOtpAttemptLeft > 0 ? (
                  <Button
                    variant="text"
                    sx={{
                      fontSize: ".875rem",
                      paddingY: ".75rem",
                      width: "fit-content",
                    }}
                    onClick={handleResendOTP}
                  >
                    Resend OTP
                  </Button>
                ) : null}
              </Box>
            </Box>
            <Box marginTop="1rem">
              {isLoading ? (
                <CustomThreeDotsLoader />
              ) : (
                <Button
                  fullWidth
                  variant="contained"
                  className="verify-otp-popup-btn"
                  disabled={otpVerifyAttemptLeft <= 0 || otpValue.length < 6}
                  onClick={handleVerifyOtpClick}
                >
                  Verify
                </Button>
              )}
            </Box>
          </Box>
        );
      case "note":
        return (
          <Box className="edit-email-note-content" width={popupWidth + "px"}>
            <img
              src={otpVerificationImage}
              alt="otp"
              style={{ width: "4.8125rem", height: "4.8125rem" }}
            />
            <Typography variant="h2" textAlign="center" marginTop="1.5rem">
              {editEmailNote?.attributes?.title}
            </Typography>
            <Typography
              variant="subtitle2"
              textAlign="center"
              marginTop=".25rem"
            >
              {editEmailNote?.attributes?.message}
            </Typography>
            <Box className="buttons-wrapper">
              <Button
                fullWidth
                variant="contained"
                className={`back-btn`}
                onClick={handleNoteBackClick}
              >
                {isBtnLoading === "Back" ? (
                  <CustomCircularLoader size={20} />
                ) : (
                  "Back"
                )}
              </Button>
              <Button
                fullWidth
                variant="contained"
                className={`confirm-btn`}
                onClick={handleConfirmClick}
              >
                {isBtnLoading === "Confirm" ? (
                  <CustomCircularLoader size={20} color="white" />
                ) : (
                  "Confirm"
                )}
              </Button>
            </Box>
          </Box>
        );
    }
  };

  return (
    <CustomPopup
      open={open}
      handleOpen={() => handleOpen(false)}
      btnLabel={"Close"}
      className="edit-profile-email-popup"
      btnStartIcon={<CloseIcon sx={{ fontSize: "1rem" }} />}
    >
      <Box padding="2rem 1rem">{renderScreens()}</Box>
    </CustomPopup>
  );
};

export default EditEmailPopup;
